import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DomesticBaseFeeCreateFormSchema,
  domesticBaseFeeSchema,
  useUpdateFee,
} from "@/api/pricings";
import { DomesticBaseFee } from "@/api.types";
import { toast } from "@/hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCustomerTiers } from "@/api/customers";
import { useDepots } from "@/api/depots";
import { useServiceAreaByDepotId } from "@/api/service-areas";
import { useState, useEffect } from "react";

interface EditDomesticBaseFeeFormProps {
  fee: DomesticBaseFee;
  onClose: () => void;
}

export function EditDomesticBaseFeeForm({
  fee,
  onClose,
}: EditDomesticBaseFeeFormProps) {
  const [selectedDepot, setSelectedDepot] = useState<string | null>(null);

  const { data: customerTiersResponse, isLoading: isLoadingTiers } =
    useCustomerTiers();
  const { data: depots = [], isLoading: isLoadingDepots } = useDepots();
  const { data: serviceAreasResponse, isLoading: isLoadingAreas } =
    useServiceAreaByDepotId(selectedDepot);

  const customerTiers = customerTiersResponse?.data ?? [];
  const safeServiceAreas = serviceAreasResponse?.data ?? [];

  // Update selectedDepot when fee data changes
  useEffect(() => {
    const depotId =
      fee.pickupServiceArea?.depotId ?? fee.deliveryServiceArea?.depotId;
    console.log("Setting depot ID:", depotId);
    if (depotId) {
      setSelectedDepot(depotId);
    }
  }, [fee]);

  const form = useForm<DomesticBaseFeeCreateFormSchema>({
    resolver: zodResolver(domesticBaseFeeSchema),
    values: {
      name: fee.name,
      basePrice: fee.basePrice,
      customerTierId: fee.customerTier?.id ?? "",
      pickupServiceAreaId: fee.pickupServiceArea?.id ?? "",
      deliveryServiceAreaId: fee.deliveryServiceArea?.id ?? "",
      isActive: fee.isActive,
    },
  });

  // For debugging
  useEffect(() => {
    console.log("Fee Data:", {
      pickupDepotId: fee.pickupServiceArea?.depotId,
      deliveryDepotId: fee.deliveryServiceArea?.depotId,
    });
    console.log("Selected Depot:", selectedDepot);
    console.log("Available Depots:", depots);
  }, [fee, selectedDepot, depots]);

  const updateFee = useUpdateFee("domestic-base", {
    onSuccess: () => {
      toast({
        title: "Fee updated successfully",
        description: "The fee has been updated successfully.",
      });
      onClose();
    },
    onError: () => {
      toast({
        title: "Failed to update fee",
        description: "The fee could not be updated.",
      });
    },
  });

  const handleDepotChange = (depotId: string) => {
    setSelectedDepot(depotId);
    form.setValue("pickupServiceAreaId", "");
    form.setValue("deliveryServiceAreaId", "");
  };

  const onSubmit = async (data: DomesticBaseFeeCreateFormSchema) => {
    await updateFee.mutateAsync({
      id: fee.id,
      data,
    });
  };

  const isLoading = isLoadingTiers || isLoadingDepots || isLoadingAreas;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Domestic Base Fee</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {isLoading ? (
              <div>Loading...</div>
            ) : (
              <>
                <FormField
                  control={form.control}
                  name="customerTierId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Tier</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select customer tier" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {customerTiers.map((tier) => (
                            <SelectItem key={tier.id} value={tier.id}>
                              {tier.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormItem>
                  <FormLabel>Depot</FormLabel>
                  <Select
                    onValueChange={handleDepotChange}
                    value={selectedDepot ?? ""}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select depot" />
                    </SelectTrigger>
                    <SelectContent>
                      {depots?.map((depot) => (
                        <SelectItem key={depot.id} value={depot.id}>
                          {depot.name} (#{depot.depotNumber})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>

                {selectedDepot && (
                  <>
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="basePrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Base Price</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              onChange={(e) =>
                                field.onChange(Number(e.target.value))
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="pickupServiceAreaId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pickup Service Area</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select pickup area" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {safeServiceAreas.map((area) => (
                                <SelectItem key={area.id} value={area.id}>
                                  {area.name} ({area.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="deliveryServiceAreaId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Delivery Service Area</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select delivery area" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {safeServiceAreas.map((area) => (
                                <SelectItem key={area.id} value={area.id}>
                                  {area.name} ({area.code})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="isActive"
                      render={({ field }) => (
                        <FormItem className="flex items-center justify-between">
                          <FormLabel>Active</FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                <div className="flex justify-end gap-2">
                  <Button variant="outline" type="button" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={updateFee.isPending}>
                    {updateFee.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
